import { FormatCurrency } from '@common/components'
import { DescriptionWithCard } from '@common/components/DescriptionWithCard'
import React, { FunctionComponent, useEffect, useState } from 'react'

import { SKUSelectorsResponse } from '@buy/components/VolumeComponentWrapper'
import { getPrice } from '@buy/utils/volume-components-helpers'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BillingPeriod } from '@common/constants'
import { createOrUpdateCheckoutSessionState, SessionKeys, setCheckoutSessionState } from '@buy/utils'
import { ParamTypes } from '@buy/Routes'
import { useParams } from 'react-router-dom'
import { isUndefined } from 'lodash-es'
import { getUsagePriceType, isFlatRateUsagePrice, isPayGoUsagePrice } from '@buy/utils/get-usage-price-values'
import { BillingTenureValue } from '../BillingPeriodSwitch'
import { LabelsType } from '../AddonsWrapper'

interface AddonProps extends SKUSelectorsResponse {
    isYearlyBillingFrequency: boolean
    labels: LabelsType
    currencyCode: string
    locale: string
    onChange: (selected: boolean, sku: string) => void
    isSelected: boolean
    isDecimal?: boolean
    isDisabled: boolean
    addonLines: Array<OrderSummaryLine>
    setAddonLines: (value: Array<OrderSummaryLine>) => void
    linkedSelectorValue: string | number
    prices: Record<string, Record<number, number>>
    usagePrice?: number
    billingTenure: BillingTenureValue
}

export const Addon: FunctionComponent<AddonProps> = ({
    sku,
    title,
    description,
    additionalInformation,
    isYearlyBillingFrequency,
    labels,
    currencyCode,
    locale,
    isDecimal = false,
    unit,
    onChange,
    isSelected,
    icon,
    isDisabled,
    addonLines,
    setAddonLines,
    linkedSelectorValue,
    prices,
    planName,
    quantityMultiplier,
    usagePrice,
    billingTenure,
}) => {
    const { pathProduct, plan } = useParams<ParamTypes>()
    const [isFlatRate, setIsFlatRate] = useState(false)
    const [isPayAsYouGo, setIsPayAsYouGo] = useState(false)

    useEffect(() => {
        const usagePriceType = getUsagePriceType(prices, usagePrice)
        if (isPayGoUsagePrice(usagePriceType)) setIsPayAsYouGo(true)
        if (isFlatRateUsagePrice(usagePriceType)) setIsFlatRate(true)
    }, [usagePrice, prices])

    const calculateAddonPrice = () => {
        if (!isUndefined(usagePrice)) {
            if (!isFlatRate) return usagePrice
            return getPrice(1, prices[BillingPeriod.MONTHLY]!)
        }
        return (
            getPrice(1, prices[isYearlyBillingFrequency ? BillingPeriod.ANNUAL : billingTenure.leftOption!]!) *
            (isYearlyBillingFrequency ? 12 : 1)
        )
    }

    const cardTitle = (
        <>
            <FormatCurrency
                value={calculateAddonPrice()}
                Locale={locale}
                CurrencyCode={currencyCode}
                decimals={isDecimal}
            />
            <span>
                {isPayAsYouGo ? (
                    unit.usagePriceLabel
                ) : (
                    <>
                        /{labels.perLabel} {unit?.unitName} /
                        {!isYearlyBillingFrequency || isFlatRate ? labels.month : labels.year}
                    </>
                )}
            </span>
        </>
    )

    const onChangeValue = (isChecked: boolean) => {
        onChange(isChecked, sku)
        createOrUpdateCheckoutSessionState({ [sku]: isChecked }, SessionKeys.addonFields, pathProduct!, plan!)
        if (isChecked) {
            const calculatedQuantity = linkedSelectorValue ? Number(linkedSelectorValue) : 0
            const newAddonLines: Array<OrderSummaryLine> = [
                ...addonLines,
                {
                    key: sku,
                    title,
                    unit: unit.unitName,
                    quantity: isPayAsYouGo ? 1 : calculatedQuantity,
                    multiplier: 1,
                    allPrices: prices,
                    skuName: planName,
                    quantityMultiplier,
                    usagePrice,
                    usagePriceLabel: unit.usagePriceLabel,
                },
            ]
            setAddonLines([...newAddonLines])
            setCheckoutSessionState(newAddonLines, SessionKeys.addonLines, pathProduct!, plan!)
        } else {
            const newAddonLines = addonLines.filter((item) => item.key !== sku)
            setAddonLines([...newAddonLines])
            setCheckoutSessionState(newAddonLines, SessionKeys.addonLines, pathProduct!, plan!)
        }
    }

    return (
        <DescriptionWithCard
            key={sku}
            icon={icon}
            title={title}
            mainDescription={description}
            cardDescription={additionalInformation}
            cardTitle={cardTitle}
            onSelect={onChangeValue}
            showCheckbox
            selected={isSelected}
            id={sku}
            disabled={isDisabled}
        />
    )
}
